import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/data';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
            {article.title}
          </h4>
          <p className="mt-1 text-xs text-foreground-500">{article.date} · {article.readTime}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
            <span className="inline-block px-3 py-1 rounded-full text-xs bg-primary-500 text-background-50 whitespace-nowrap">
              精选推荐
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-2">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.date}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}